// External Dependencies
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';
import { DisplayAggregateQuestionDto } from '@keyops-cep/api-client';
import { toPng } from 'html-to-image';

// Internal Dependencies
import { colors } from '../../../assets/custom-theme';
import { ISurveySparrowAnswerData } from '../../../pages/EngagementDetails/types';
import {
  computeMatrixComponentAverages,
  getHeaderLabelsFromChoices,
} from '../../../utils/functions/visualization-utils';
import i18n from '../../../utils/i18n';
import { VisualizationChartRef } from '..';

type MultiSelectNumericGridProps = {
  question: DisplayAggregateQuestionDto;
  questionAnswers: (ISurveySparrowAnswerData | undefined)[];
};

interface Cell {
  label?: string;
  value: number;
  percent: string;
}

type MatrixRow = Cell[];
type MatrixBody = MatrixRow[];

const cellStyle = {
  textTransform: 'capitalize',
  fontSize: '12px',
  padding: '8px',
  wordWrap: 'break-word',
  whiteSpace: 'normal',
};

const colorPalette = {
  low: colors.keyops.gray500,
  mid: colors.keyops.blue,
  high: colors.keyops.teal,
};

const MultiSelectNumericGrid = forwardRef<
  VisualizationChartRef,
  MultiSelectNumericGridProps
>(({ question, questionAnswers }, ref) => {
  const tableRef = useRef<HTMLDivElement | null>(null);
  const [matrix, setMatrix] = useState<MatrixBody>([]);

  // Memoize values that only depend on the question
  const {
    headers,
    headerLabels,
    minVal,
    maxVal,
    lowThreshold,
    highThreshold,
    components,
  } = useMemo(() => {
    // Extract min and max from numeric labels in choices
    const numericChoices = (question.choices ?? [])
      .map((c) => parseFloat(c.label.match(/\d+/)?.[0] ?? ''))
      .filter((v) => !isNaN(v));
    const minVal = numericChoices.length ? Math.min(...numericChoices) : 1;
    const maxVal = numericChoices.length ? Math.max(...numericChoices) : 7;

    const rangeSize = maxVal - minVal;
    const lowThreshold = Math.round(minVal + rangeSize / 3);
    const highThreshold = Math.round(minVal + (2 * rangeSize) / 3);

    // Pull header values from choices
    const headerLabels = getHeaderLabelsFromChoices(question.choices);
    const headers: React.ReactElement[] = [
      <Th key="header-label" sx={cellStyle}></Th>,
      ...headerLabels.map((type, i) => (
        <Th key={`header-${i}`} sx={cellStyle}>
          {type}
        </Th>
      )),
    ];

    return {
      headers,
      headerLabels,
      minVal,
      maxVal,
      lowThreshold,
      highThreshold,
      components: question.components ?? [],
    };
  }, [question]);

  const getCellColor = (value: number): string => {
    if (isNaN(value)) return 'transparent';

    if (value <= lowThreshold) return colorPalette.low;
    if (value <= highThreshold) return colorPalette.mid;
    return colorPalette.high;
  };

  useEffect(() => {
    // Calculate answer averages
    const averages = computeMatrixComponentAverages(
      questionAnswers,
      headerLabels
    );

    // Build the matrix
    const matrix: MatrixBody = components.map((statement) => {
      // Find the corresponding average value
      const average = averages.find(
        (a) => a.componentId === statement.surveySparrowId
      );

      const row: MatrixRow = [
        { label: statement.label, value: 0, percent: '' },
        // For each header column, insert the average value if available
        ...headerLabels.map((type) => {
          const avg = average?.[type];
          if (typeof avg === 'number') {
            return { value: avg, percent: '', label: '' };
          }
          // Fallback on N/A if the average is not available
          return { value: 0, percent: '', label: 'N/A' };
        }),
      ];

      return row;
    });

    setMatrix(matrix);
  }, [questionAnswers, headerLabels, components]);

  useImperativeHandle(ref, () => {
    const createImageFromTable = async (): Promise<string | null> => {
      if (!tableRef.current) return null;

      // Create a clone of the table
      const clone = tableRef.current.cloneNode(true) as HTMLDivElement;

      // Get computed width of the original table
      const originalTableWidth = getComputedStyle(tableRef.current).width;

      // Make the clone fully visible and hide the scrollbars
      clone.style.height = 'auto';
      clone.style.maxHeight = 'none';
      clone.style.overflowY = 'visible';
      clone.style.overflow = 'hidden'; // Hide scrollbars
      clone.style.width = originalTableWidth; // Set width of the clone to match original table
      const style = document.createElement('style'); // Style the legend
      style.textContent = `
        .legend {
          margin-top: 0 !important;
        }
        .legend-inner {
          margin-left: 16px !important;
        }
      `;
      clone.appendChild(style);

      // Append the clone to the body
      document.body.appendChild(clone);

      try {
        // Take screenshot of the clone
        const dataUrl = await toPng(clone);
        return dataUrl;
      } catch (error) {
        console.error('oops, something went wrong!', error);
      } finally {
        // Remove the clone from the body
        document.body.removeChild(clone);
      }

      return null;
    };

    return {
      onDownload: async (name: string) => {
        const dataUrl = await createImageFromTable();
        if (dataUrl) {
          // Download the screenshot
          const link = document.createElement('a');
          link.download = `${name}.png`;
          link.href = dataUrl;
          link.click();
        }
      },
      onCopyToClipboard: async () => {
        const dataUrl = await createImageFromTable();
        if (dataUrl) {
          try {
            // Convert data URL to Blob
            const response = await fetch(dataUrl);
            const blob = await response.blob();
            const item = new ClipboardItem({ 'image/png': blob });
            await navigator.clipboard.write([item]);
            console.log('Image copied to clipboard');
          } catch (error) {
            console.error('oops, something went wrong!', error);
          }
        }
      },
    };
  });

  return (
    <Box
      ref={tableRef}
      display="flex"
      flexDirection="column"
      h="100%"
      boxSizing="border-box"
    >
      <Box flex="1" minH={0} overflowY="auto">
        <TableContainer
          bg={useColorModeValue('white', 'gray.700')}
          borderWidth="thin"
          borderColor="gray.200"
          borderRadius="lg"
          overflowX="auto"
          boxSizing="border-box"
        >
          <Table variant="simple" key={`matrix-${question.id}-table`}>
            <Thead>
              <Tr>{headers}</Tr>
            </Thead>
            <Tbody>
              {matrix.map((row: MatrixRow, i: number) => (
                <Tr key={`matrix-${question.id}-row-${i}`}>
                  {row.map((cell: Cell, j: number) => (
                    <Td
                      key={`matrix-${question.id}-cell-${i}-${j}`}
                      sx={{
                        ...cellStyle,
                        backgroundColor: cell.label
                          ? 'transparent'
                          : getCellColor(cell.value),
                        color: cell.label ? undefined : 'black',
                        fontWeight: cell.label ? 'bold' : 'normal',
                        textAlign: 'center',
                        borderRight:
                          j < row.length - 1 ? '1px solid #E2E8F0' : 'none',
                      }}
                    >
                      {cell.label ? cell.label : cell.value}
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>

      {/* Sticky legend */}
      <Box
        className="legend" // used to inject style for export/copy functions
        mt={2}
        py={2}
        borderTop="1px solid"
        borderColor="gray.200"
        bg={useColorModeValue('white', 'gray.700')}
        position="sticky"
        bottom="0"
        zIndex={1}
      >
        <Box
          className="legend-inner"
          display="flex"
          gap={3}
          alignItems="center"
          fontSize="sm"
        >
          <Box w="16px" h="16px" bg={colorPalette.low} borderRadius="sm" />
          <Box>
            {i18n.t('visualization.multiGridMatrix.low')} ({minVal}–
            {lowThreshold})
          </Box>
          <Box w="16px" h="16px" bg={colorPalette.mid} borderRadius="sm" />
          <Box>
            {i18n.t('visualization.multiGridMatrix.mid')} ({lowThreshold}–
            {highThreshold})
          </Box>
          <Box w="16px" h="16px" bg={colorPalette.high} borderRadius="sm" />
          <Box>
            {i18n.t('visualization.multiGridMatrix.high')} ({highThreshold}–
            {maxVal})
          </Box>
        </Box>
      </Box>
    </Box>
  );
});

MultiSelectNumericGrid.displayName = 'MultiSelectNumericGrid';
export default MultiSelectNumericGrid;
